import {
    FACE_DETECTOR_MODEL_PATH,
    FACE_LANDMARKS_MODEL_PATH,
    IQA_BLUR_MODEL_PATH,
} from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/face-detector-provider/utils';
import type {
    LivenessBatchFrameMetadata,
    LivenessBatchModelVersions,
} from '@modules/docs-verification-widget/api/requests/liveness-batch-metadata';
import { getEnvVar } from '@utils/get-env-var';

import {
    type PipelineResult,
    computeAvgEar,
    EAR_THR,
    PITCH_THR,
    ROLL_THR,
    YAW_THR,
} from './face-pipeline';
import {
    type ImageQualityResult,
    BLUR_NN_THRESHOLD,
    HIGH_ILLUMINATION_THRESHOLD,
    IMG_RESOLUTION_THRESHOLD,
    LOW_ILLUMINATION_THRESHOLD,
} from './image-quality-pipeline';

export const buildDeviceMetadata = (): NonNullable<
    LivenessBatchFrameMetadata['device_metadata']
> => ({
    platform: 'WEB',
    app_version: getEnvVar('REACT_APP_WIDGET_VERSION') || null,
    os_version: typeof navigator === 'undefined' ? null : navigator.platform || null,
    device_model: typeof navigator === 'undefined' ? null : navigator.userAgent || null,
});

export const buildModelVersions = (): LivenessBatchModelVersions => ({
    iqa_blur_model: IQA_BLUR_MODEL_PATH,
    mediapipe_model: FACE_LANDMARKS_MODEL_PATH,
    other_models: {
        face_detector: FACE_DETECTOR_MODEL_PATH,
    },
});

export const buildFrameMetadata = ({
    frameId,
    timestamp,
    pipelineResult,
    imageQualityResult,
}: {
    frameId: string;
    timestamp: number;
    pipelineResult?: PipelineResult | null;
    imageQualityResult?: ImageQualityResult | null;
}): LivenessBatchFrameMetadata => {
    const ear =
        pipelineResult?.landmarks && pipelineResult.landmarks.length
            ? computeAvgEar(pipelineResult.landmarks)
            : undefined;

    return {
        frame_id: frameId,
        timestamp: Math.round(timestamp),
        device_metadata: buildDeviceMetadata(),
        model_versions: buildModelVersions(),
        iqa_results: imageQualityResult
            ? {
                  scores: {
                      blur_prob: imageQualityResult.blurScore,
                      dark_score: imageQualityResult.darkScore,
                      light_score: imageQualityResult.lightScore,
                      resolution: Math.round(imageQualityResult.resolutionScore),
                  },
                  thresholds: {
                      blur_threshold: BLUR_NN_THRESHOLD,
                      dark_threshold: LOW_ILLUMINATION_THRESHOLD,
                      light_threshold: HIGH_ILLUMINATION_THRESHOLD,
                      resolution_threshold: Math.round(IMG_RESOLUTION_THRESHOLD),
                  },
              }
            : null,
        mediapipe_results: pipelineResult
            ? {
                  face_box: pipelineResult.box
                      ? pipelineResult.box.map((value) => Math.round(value))
                      : null,
                  head_pose: pipelineResult.pose
                      ? {
                            pitch: pipelineResult.pose.pitch,
                            yaw: pipelineResult.pose.yaw,
                            roll: pipelineResult.pose.roll,
                            pitch_threshold: PITCH_THR,
                            yaw_threshold: YAW_THR,
                            roll_threshold: ROLL_THR,
                        }
                      : null,
                  eye_metrics:
                      ear === undefined
                          ? null
                          : {
                                eye_aspect_ratio: ear,
                                eye_aspect_ratio_threshold: EAR_THR,
                            },
              }
            : null,
    };
};
